import type {
  ApiResponse,
  MembershipBenefitsSchemaType,
  MembershipSchemaType,
} from '@asetquhub/validation';
import { api } from '../lib/axios';

// Export types from validation package
export type PrimeAccess = MembershipSchemaType;
export type PrimeAccessBenefits = MembershipBenefitsSchemaType;

export type PrimeAccessWithBenefits = MembershipSchemaType & {
  benefits: MembershipBenefitsSchemaType | null;
};

/**
 * Prime Access purchase request with signature
 */
export type PrimeAccessPurchaseRequest = {
  membershipId: string;
  message: string;
  signature: string;
};

/**
 * Prime Access purchase response
 */
export type PrimeAccessPurchaseResponse = {
  txHash: string;
  transactionId: string;
  status: 'BROADCASTING';
  membershipId: string;
  amount: string;
  amountRaw: string;
  message: string;
};

class PrimeAccessService {
  /**
   * Get all active Prime Access plans
   */
  async getPlans(params?: { status?: 'ACTIVE' | 'INACTIVE' }): Promise<PrimeAccess[]> {
    const response = await api.get<ApiResponse<PrimeAccess[]>>('/api/memberships', {
      params,
    });
    return response.data.data!;
  }

  /**
   * Get Prime Access plan by ID
   */
  async getPlan(id: string): Promise<PrimeAccess> {
    const response = await api.get<ApiResponse<PrimeAccess>>(`/api/memberships/${id}`);
    return response.data.data!;
  }

  /**
   * Get Prime Access benefits
   */
  async getBenefits(planId: string): Promise<PrimeAccessBenefits> {
    const response = await api.get<ApiResponse<PrimeAccessBenefits>>(
      `/api/memberships/${planId}/benefits`
    );
    return response.data.data!;
  }

  /**
   * Get user's active Prime Access with benefits
   */
  async getUserActivePlan(): Promise<PrimeAccessWithBenefits | null> {
    try {
      const response = await api.get<ApiResponse<PrimeAccessWithBenefits>>(
        '/api/memberships/my-membership'
      );
      return response.data.data!;
    } catch (error) {
      // Return null if user has no membership
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw new Error('Failed to get user active Prime Access');
    }
  }

  /**
   * Purchase Prime Access with wallet signature (gas relay)
   */
  async purchase(data: PrimeAccessPurchaseRequest): Promise<PrimeAccessPurchaseResponse> {
    const response = await api.post<ApiResponse<PrimeAccessPurchaseResponse>>(
      `/api/memberships/${data.membershipId}/purchase`,
      {
        message: data.message,
        signature: data.signature,
      }
    );
    return response.data.data!;
  }

  /**
   * Get user's Prime Access purchase history
   */
  async getUserPurchases(): Promise<
    Array<{
      id: string;
      membershipId: string;
      membershipName: string;
      membershipTier: string;
      amount: string;
      txHash: string;
      status: string;
      createdAt: number;
      invoiceNo?: string;
      biAccessStatus?: string;
      biAccessStartAt?: number;
      biAccessEndAt?: number;
    }>
  > {
    const response = await api.get<
      ApiResponse<
        Array<{
          id: string;
          membershipId: string;
          membershipName: string;
          membershipTier: string;
          amount: string;
          txHash: string;
          status: string;
          createdAt: number;
          invoiceNo?: string;
          biAccessStatus?: string;
          biAccessStartAt?: number;
          biAccessEndAt?: number;
        }>
      >
    >('/api/memberships/my/purchases');
    return response.data.data!;
  }
}

export const primeAccessService = new PrimeAccessService();
